import { useActiveTenant } from '@/hooks/useActiveTenant';
import { Icon } from '@iconify/react';
import { Button, Chip, Spinner, Textarea } from '@nextui-org/react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateTenantConfig,
  usePatchTenantConfig,
  useTenantConfig,
} from '../../hooks/queries/tenant-config';
import { DashboardTab } from '../../services/tenant-config-service';

export default function TenantConfigPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { tenantId } = useActiveTenant();

  const { data: tenantConfig, isLoading, refetch } = useTenantConfig(tenantId!);
  const createConfig = useCreateTenantConfig();
  const patchConfig = usePatchTenantConfig(tenantConfig?.id || 0);

  // Inline editing state
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editingMessage, setEditingMessage] = useState('');
  const [isEditingPromoTitle, setIsEditingPromoTitle] = useState(false);
  const [editingPromoTitle, setEditingPromoTitle] = useState('');

  const handleEditMessage = () => {
    setEditingMessage(tenantConfig?.agent_first_message || '');
    setIsEditingMessage(true);
  };

  const handleSaveMessage = async () => {
    if (!editingMessage.trim()) {
      toast.error('Agent first message cannot be empty');
      return;
    }

    try {
      if (tenantConfig?.id) {
        // Update existing configuration
        await patchConfig.mutateAsync({
          agent_first_message: editingMessage,
        });
      } else {
        // Create new configuration
        await createConfig.mutateAsync({
          tenant: tenantId!,
          agent_first_message: editingMessage,
          dashboard_config: { tabs: [] },
        });
      }
      setIsEditingMessage(false);
      toast.success('Agent message updated successfully');
    } catch (error) {
      console.error('Error updating message:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMessage(false);
    setEditingMessage('');
  };

  const handleEditPromoTitle = () => {
    setEditingPromoTitle(
      tenantConfig?.dashboard_config?.promotion_bar_title || ''
    );
    setIsEditingPromoTitle(true);
  };

  const handleSavePromoTitle = async () => {
    try {
      const promoTitleValue = editingPromoTitle.trim() || null;

      if (tenantConfig?.id) {
        // Update existing configuration
        await patchConfig.mutateAsync({
          dashboard_config: {
            ...tenantConfig.dashboard_config,
            promotion_bar_title: promoTitleValue,
          },
        });
      } else {
        // Create new configuration
        await createConfig.mutateAsync({
          tenant: tenantId!,
          agent_first_message: '',
          dashboard_config: {
            tabs: [],
            promotion_bar_title: promoTitleValue,
          },
        });
      }
      setIsEditingPromoTitle(false);
      toast.success('Promotion bar title updated successfully');
    } catch (error) {
      console.error('Error updating promotion bar title:', error);
    } finally {
      await refetch();
    }
  };

  const handleCancelPromoTitleEdit = () => {
    setIsEditingPromoTitle(false);
    setEditingPromoTitle('');
  };

  const handleConfigureTabs = () => {
    navigate(`/${tenantSlug}/tenant-config/tabs`);
  };

  const tabs = tenantConfig?.dashboard_config?.tabs || [];
  const sortedTabs = [...tabs].sort(
    (a: DashboardTab, b: DashboardTab) => a.priority - b.priority
  );

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 p-2 sm:p-8 sm:pb-0 pb-0">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold mb-2">Tenant Configuration</h1>
          {/* <p className="text-default-600">
          Configure your tenant settings and dashboard layout
        </p> */}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2 pt-0 sm:p-8 sm:pt-0">
        <div className="space-y-8">
          {/* Agent Configuration */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Icon
                  icon="heroicons:chat-bubble-left-right"
                  className="text-primary w-5 h-5"
                />
              </div>
              <div>
                <h3 className="text-md font-semibold">Agent Configuration</h3>
                <p className="text-xs text-default-600">
                  Configure how your AI agent behaves and communicates
                </p>
              </div>
            </div>

            <div className="max-w-2xl">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-default-700">
                    First Message
                  </label>
                  {!isEditingMessage && (
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<Icon icon="heroicons:pencil" width={14} />}
                      onClick={handleEditMessage}
                    >
                      Edit
                    </Button>
                  )}
                </div>

                {isEditingMessage ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editingMessage}
                      onChange={(e) => setEditingMessage(e.target.value)}
                      placeholder="Enter the first message your agent will send to users..."
                      minRows={3}
                      maxRows={6}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        color="primary"
                        onClick={handleSaveMessage}
                        isLoading={
                          patchConfig.isPending || createConfig.isPending
                        }
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="bordered"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-default-100 rounded-lg">
                    <p className="text-sm text-foreground">
                      {tenantConfig?.agent_first_message || (
                        <span className="text-default-500 italic">
                          No first message configured. Click edit to add one.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Promotion Bar Configuration */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-warning-100 rounded-lg">
                <Icon
                  icon="heroicons:megaphone"
                  className="text-warning w-5 h-5"
                />
              </div>
              <div>
                <h3 className="text-md font-semibold">Promotion Bar</h3>
                <p className="text-xs text-default-600">
                  Configure a promotional message to display at the top of the
                  dashboard
                </p>
              </div>
            </div>

            <div className="max-w-2xl">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-default-700">
                    Promotion Bar Title
                  </label>
                  {!isEditingPromoTitle && (
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<Icon icon="heroicons:pencil" width={14} />}
                      onClick={handleEditPromoTitle}
                    >
                      Edit
                    </Button>
                  )}
                </div>

                {isEditingPromoTitle ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editingPromoTitle}
                      onChange={(e) => setEditingPromoTitle(e.target.value)}
                      placeholder="Enter a promotional message to display on the dashboard..."
                      minRows={2}
                      maxRows={4}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        color="primary"
                        onClick={handleSavePromoTitle}
                        isLoading={
                          patchConfig.isPending || createConfig.isPending
                        }
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="bordered"
                        onClick={handleCancelPromoTitleEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-default-100 rounded-lg">
                    <p className="text-sm text-foreground">
                      {tenantConfig?.dashboard_config?.promotion_bar_title || (
                        <span className="text-default-500 italic">
                          No promotion bar title configured. Click edit to add
                          one.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Dashboard Configuration */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary-100 rounded-lg">
                  <Icon
                    icon="heroicons:squares-2x2"
                    className="text-secondary w-5 h-5"
                  />
                </div>
                <div>
                  <h3 className="text-md font-semibold">Dashboard Tabs</h3>
                  <p className="text-xs text-default-600">
                    {tabs.length > 0
                      ? `${tabs.length} custom ${tabs.length === 1 ? 'tab' : 'tabs'} configured`
                      : 'Using default tabs - no custom configuration'}
                  </p>
                </div>
              </div>
              <Button
                color="secondary"
                variant="flat"
                startContent={<Icon icon="heroicons:cog-6-tooth" width={14} />}
                onClick={handleConfigureTabs}
              >
                Configure Tabs
              </Button>
            </div>

            {tabs.length > 0 ? (
              <div className="max-h-[120px] overflow-x-auto border border-default-200 rounded-lg p-2">
                <div className="flex gap-2 pb-2">
                  {sortedTabs.map((tab: DashboardTab) => (
                    <div
                      key={tab.id}
                      className="flex-none flex items-center gap-2 p-2 bg-default-100  border-default-200 rounded-lg"
                    >
                      <span className="text-xs font-medium text-default-600">
                        #{tab.priority}
                      </span>
                      <span className="text-sm font-medium whitespace-nowrap">
                        {tab.label}
                      </span>
                      <Chip size="sm" variant="flat" color="secondary">
                        {tab.tag}
                      </Chip>
                      <Chip
                        size="sm"
                        variant="dot"
                        color={tab.is_active ? 'success' : 'default'}
                      >
                        {tab.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-default-50 rounded-lg border border-dashed border-default-300">
                <Icon
                  icon="heroicons:squares-plus"
                  className="mx-auto text-default-400 mb-3"
                  width={32}
                />
                <h4 className="text-sm font-medium text-default-700 mb-2">
                  No Custom Tabs Configured
                </h4>
                <p className="text-xs text-default-600 mb-4">
                  Default tabs are being used. Configure custom tabs to override
                  them.
                </p>
                <Button
                  size="sm"
                  color="secondary"
                  variant="flat"
                  onClick={handleConfigureTabs}
                >
                  Configure Custom Tabs
                </Button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
